/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { BaseService } from "@/services/BaseService";
import { UserPhone } from "@/services/api/userphone/UserPhoneService";

// Define your types here instead of importing from models/User
export interface UserListModel {
    userId: number;
    userFirstName: string;
    userLastName: string;
    email: string;
    jobTitle: string;
    officeLocation: string;
    department: string | null;
    departmentId: number | null;
    organization: string;
    organizationId: number;
    isActive: boolean;
    isAccountLocked: boolean;
    userType: string;
    createdOn: string | null; // ISO date string
    modifiedOn: string | null; // ISO date string
    phoneNumbers?: UserPhone[]; // NEW: Phone numbers from UserPhone table
    kronosEmployeeId?: string | null; // Story #31457: Live Map Kronos overtime join key
}

// Enum for User Creation Type
export enum UserCreationType {
    Regular = 1, // Gets Requestor role, web portal access
    Driver = 2, // Gets Driver role, mobile app access only
}

export interface UserCreateModel {
    userFirstName: string;
    userLastName: string;
    email: string;
    jobTitle: string;
    officeLocation: string;
    departmentId?: number | null;
    organizationId: number;
    isActive?: boolean;
    userType: UserCreationType; // NEW FIELD
}

export interface UserEditModel {
    userFirstName: string;
    userLastName: string;
    email: string;
    jobTitle: string;
    officeLocation: string;
    departmentId?: number | null;
    organizationId: number;
    isActive?: boolean;
    userType?: UserCreationType; // Optional for edit mode
}

// Interface for status update only
export interface UserStatusUpdateModel {
    isActive: boolean;
}

// Interface that matches the API endpoint exactly
interface ApiCreateUserRequest {
    organizationId: number;
    departmentId: number;
    firstName: string;
    lastname: string; // Note: lowercase 'n' as shown in API docs
    email: string;
    jobTitle: string;
    officeLocation: string;
    userType: UserCreationType; // NEW FIELD
}

// Response interfaces for the new API structure
interface CreateUserSuccessResponse {
    message: string;
}

interface CreateUserErrorResponse {
    message: string;
}

class ManageUserServiceClass extends BaseService {
    protected get baseUrl(): string {
        return `${process.env.NEXT_PUBLIC_API_URL}/api/User/`;
    }

    async getAllUsers(): Promise<UserListModel[]> {
        return this.get("all");
    }

    async createUser(
        data: UserCreateModel
    ): Promise<CreateUserSuccessResponse> {
        // Transform the frontend model to match the API expectations
        const apiData: ApiCreateUserRequest = {
            organizationId: data.organizationId,
            departmentId: data.departmentId || 0, // Default to 0 if null/undefined
            firstName: data.userFirstName,
            lastname: data.userLastName, // Note: lowercase 'n'
            email: data.email,
            jobTitle: data.jobTitle,
            officeLocation: data.officeLocation,
            userType: data.userType, // NEW FIELD
        };

        try {
            const result = await this.post("external-createUser", apiData);
            return result as CreateUserSuccessResponse;
        } catch (error: any) {
            // Enhanced error handling for different HTTP status codes
            let errorMessage = "Failed to create user";

            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;

                switch (status) {
                    case 400:
                        // Bad Request - validation errors
                        errorMessage =
                            data?.message || "Invalid input data provided";
                        break;
                    case 409:
                        // Conflict - user already exists
                        errorMessage =
                            data?.message ||
                            "A user with this email address already exists";
                        break;
                    case 500:
                        // Internal Server Error
                        errorMessage =
                            data?.message ||
                            "An unexpected server error occurred";
                        break;
                    default:
                        if (typeof data === "string") {
                            errorMessage = data;
                        } else if (data?.message) {
                            errorMessage = data.message;
                        } else if (data?.error) {
                            errorMessage = data.error;
                        }
                        break;
                }
            } else if (error.request) {
                errorMessage =
                    "No response from server. Please check your connection.";
            } else {
                errorMessage = error.message || "Unknown error occurred";
            }

            throw new Error(errorMessage);
        }
    }

    async updateUser(
        userId: number,
        data: UserEditModel
    ): Promise<UserListModel> {
        return this.put(`update/${userId}`, data);
    }

    async updateUserDepartment(
        email: string,
        departmentId: number
    ): Promise<{ message: string }> {
        return this.put("update-department", {
            email,
            departmentId,
        });
    }

    async updateUserStatus(
        userId: number,
        isActive: boolean
    ): Promise<UserListModel> {
        try {
            // Use the correct API endpoint: PUT /api/User/ToggleIsActive/{userId}
            // This endpoint toggles the current status, no request body needed
            const result = await this.put(`ToggleIsActive/${userId}`);
            return result as UserListModel;
        } catch (error: any) {
            throw new Error(`Failed to toggle user status: ${error.message}`);
        }
    }

    async updateKronosEmployeeId(
        userId: number,
        kronosEmployeeId: string | null
    ): Promise<{ userId: number; kronosEmployeeId: string | null }> {
        return this.put(`${userId}/kronos-employee-id`, { kronosEmployeeId });
    }

    // Note: Delete functionality removed as per requirements
}

export const manageUserService = new ManageUserServiceClass();
